/**
 * Modules — the pure half of DEC-06 self-management (bata-ui#166 W1-7, `docs/adr/2006`).
 *
 *   GET  /api/v1/foundations/modules?country_key=     each module's effective settings, who approved them
 *   GET  /api/v1/registry/parameter-keys?as_of=       the cadence vocabulary (the card's `vocabulary`)
 *   POST /api/v1/registry/changes                     a change is a PROPOSAL; someone else approves it
 *
 * A module (`inseason_replenishment`, `inseason_price`, `exit_consolidation`) is paused, re-timed or
 * narrowed by registry rows per country — `module.<m>.enabled` (1/0), `module.<m>.cadence`
 * (`daily` | `weekly:<weekday>`), `module.<m>.excluded_doors` (comma-separated door keys; not
 * declared for price, whose steps carry no door). Nothing here is a button that runs or stops a
 * job: a change is a proposal an approver other than its author applies (DEC-03, family `module`).
 *
 * Everything decided in the browser is decided here, on strings, and tested:
 * - the change set carries only the settings the planner changed, each at the market's scope;
 * - the cadence is picked from the vocabulary the catalogue publishes, never typed and never a
 *   list this build carries (no vocabulary published = no cadence change offered);
 * - excluded doors are normalised (trimmed, de-duplicated, sorted) as the platform stores them;
 * - no default for any value, owner, rationale or date the planner has not stated.
 */

import type { ModuleSettingView, ModuleStatus, ParameterKeyEntry, ProposeChangeParams, RevisionRequest } from '@/api/generated';
import { doorKeys } from '@/planning';

import { MINIMUM_RATIONALE_WORDS, rationaleWordCount } from './parameters';

/** The modules the platform declares, in its order — each one's name in the catalogue. */
export const MODULE_KEY: Readonly<Record<string, string>> = {
  inseason_replenishment: 'foundations.modules.module.inseasonReplenishment',
  inseason_price: 'foundations.modules.module.inseasonPrice',
  exit_consolidation: 'foundations.modules.module.exitConsolidation',
};

export function moduleKey(module: string): string {
  return MODULE_KEY[module] ?? 'foundations.modules.module.other';
}

export const STATE_KEY: Readonly<Record<string, string>> = {
  enabled: 'foundations.modules.state.enabled',
  disabled: 'foundations.modules.state.disabled',
  cadence_invalid: 'foundations.modules.state.cadenceInvalid',
};

export function moduleStateKey(state: string): string {
  return STATE_KEY[state] ?? 'foundations.modules.state.other';
}

export const SOURCE_KEY: Readonly<Record<string, string>> = {
  country: 'foundations.modules.source.country',
  estate: 'foundations.modules.source.estate',
  default: 'foundations.modules.source.default',
};

export function sourceKey(source: string): string {
  return SOURCE_KEY[source] ?? 'foundations.modules.source.other';
}

/** The registry keys of one module, derived from its settings (the read names them). */
export interface ModuleKeys {
  readonly enabled: string;
  readonly cadence: string;
  /** `null` for a module with no excluded-doors key (price). */
  readonly excludedDoors: string | null;
}

export function moduleKeys(module: ModuleStatus): ModuleKeys {
  return {
    enabled: module.enabled.parameterKey,
    cadence: module.cadence.parameterKey,
    excludedDoors: module.excludedDoors?.parameterKey ?? null,
  };
}

/** The cadence vocabulary the catalogue publishes for a key, or `null` when it publishes none. */
export function cadenceVocabulary(entries: readonly ParameterKeyEntry[], cadenceKey: string): readonly string[] | null {
  const vocabulary = entries.find((entry) => entry.parameterKey === cadenceKey)?.vocabulary;
  return vocabulary === null || vocabulary === undefined || vocabulary.length === 0 ? null : vocabulary;
}

/**
 * A cadence value in words: `daily`, `weekly:<weekday>` (Monday… Sunday), or `monthly:<day>`
 * (day 1–28 of each calendar month; bata-platform ADR 6075, bata-reqs issue 35 P0.3), else verbatim.
 */
export type CadenceWords =
  | { readonly kind: 'everyTick' }
  | { readonly kind: 'daily' }
  | { readonly kind: 'weekly'; readonly weekday: number }
  | { readonly kind: 'monthly'; readonly day: number }
  | { readonly kind: 'verbatim'; readonly value: string };

/** Monday first, as the platform's `WEEKDAYS`; the index is days after Monday. */
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export function cadenceWords(value: string | null): CadenceWords {
  if (value === null) return { kind: 'everyTick' };
  if (value === 'daily') return { kind: 'daily' };
  // Days 1–28 only, as the platform accepts them: every month has the day.
  const monthly = /^monthly:(\d{1,2})$/.exec(value);
  if (monthly !== null) {
    const day = Number(monthly[1]);
    return day >= 1 && day <= 28 ? { kind: 'monthly', day } : { kind: 'verbatim', value };
  }
  const match = /^weekly:([a-z]+)$/.exec(value);
  const index = match === null ? -1 : WEEKDAYS.indexOf(match[1] as (typeof WEEKDAYS)[number]);
  return index < 0 ? { kind: 'verbatim', value } : { kind: 'weekly', weekday: index };
}

/** The door keys a text value names — shared with the parameter controls (`docs/adr/9024`). */
export { doorKeys };

/** "paused since X, approved by Y" needs these four facts off a setting. */
export function settingApproval(setting: ModuleSettingView): {
  readonly since: string | null;
  readonly approvedBy: readonly string[];
  readonly approvedAt: string | null;
  readonly recordedBy: string | null;
} {
  return { since: setting.validFrom, approvedBy: setting.approvedBy, approvedAt: setting.approvedAt, recordedBy: setting.recordedBy };
}

// ── the proposal ─────────────────────────────────────────────────────────────

export type EnabledChoice = 'unchanged' | 'on' | 'off';

export interface ModuleChangeDraft {
  readonly enabled: EnabledChoice;
  /** `''` = unchanged; otherwise one value of the published vocabulary. */
  readonly cadence: string;
  /** Whether the excluded doors change at all — an empty list is a real value ("none"). */
  readonly changeDoors: boolean;
  readonly doorsText: string;
  readonly validFrom: string;
  readonly validTo: string;
  readonly reviewDueOn: string;
  readonly owner: string;
  readonly rationale: string;
}

export const BLANK_MODULE_CHANGE: ModuleChangeDraft = {
  enabled: 'unchanged', cadence: '', changeDoors: false, doorsText: '',
  validFrom: '', validTo: '', reviewDueOn: '', owner: '', rationale: '',
};

export type ModuleChangeRefusal =
  | 'nothing_changed' | 'cadence_not_in_vocabulary' | 'no_owner' | 'no_rationale' | 'rationale_too_short'
  | 'no_review_date' | 'no_valid_from' | 'no_valid_to' | 'no_doors_key';

export type ModuleChangeComposition =
  | { readonly ok: true; readonly params: ProposeChangeParams; readonly revisions: readonly RevisionRequest[] }
  | { readonly ok: false; readonly refusal: ModuleChangeRefusal };

/**
 * The change set a module draft proposes, at the market's scope — or the one reason it cannot be
 * composed. Only the settings the planner changed become revisions; a draft that changes nothing
 * is refused rather than proposing a set with no effect.
 */
export function composeModuleChange(
  draft: ModuleChangeDraft,
  keys: ModuleKeys,
  country: string,
  vocabulary: readonly string[] | null,
  changeId: string,
): ModuleChangeComposition {
  const revisions: RevisionRequest[] = [];
  const at = (parameterKey: string, value: { numericValue: number | null; textValue: string | null }): RevisionRequest => ({
    parameterKey, scopeDimension: 'country', scopeValue: country, ...value, validFrom: draft.validFrom, validTo: draft.validTo,
  });
  if (draft.enabled !== 'unchanged') revisions.push(at(keys.enabled, { numericValue: draft.enabled === 'on' ? 1 : 0, textValue: null }));
  if (draft.cadence !== '') {
    if (vocabulary === null || !vocabulary.includes(draft.cadence)) return { ok: false, refusal: 'cadence_not_in_vocabulary' };
    revisions.push(at(keys.cadence, { numericValue: null, textValue: draft.cadence }));
  }
  if (draft.changeDoors) {
    if (keys.excludedDoors === null) return { ok: false, refusal: 'no_doors_key' };
    revisions.push(at(keys.excludedDoors, { numericValue: null, textValue: doorKeys(draft.doorsText).join(',') }));
  }
  if (revisions.length === 0) return { ok: false, refusal: 'nothing_changed' };
  if (draft.owner.trim() === '') return { ok: false, refusal: 'no_owner' };
  if (draft.rationale.trim() === '') return { ok: false, refusal: 'no_rationale' };
  if (rationaleWordCount(draft.rationale) < MINIMUM_RATIONALE_WORDS) return { ok: false, refusal: 'rationale_too_short' };
  if (draft.reviewDueOn.trim() === '') return { ok: false, refusal: 'no_review_date' };
  if (draft.validFrom.trim() === '') return { ok: false, refusal: 'no_valid_from' };
  if (draft.validTo.trim() === '') return { ok: false, refusal: 'no_valid_to' };
  return {
    ok: true,
    revisions,
    params: {
      body: {
        changeId,
        owner: draft.owner.trim(),
        rationale: draft.rationale.trim(),
        reviewDueOn: draft.reviewDueOn,
        evidenceRef: null,
        revisions,
      },
    },
  };
}

export const MODULE_REFUSAL_KEY: Readonly<Record<ModuleChangeRefusal, string>> = {
  nothing_changed: 'foundations.modules.refusal.nothingChanged',
  cadence_not_in_vocabulary: 'foundations.modules.refusal.cadenceNotInVocabulary',
  no_owner: 'foundations.parameters.proposal.refusal.noOwner',
  no_rationale: 'foundations.parameters.proposal.refusal.noRationale',
  rationale_too_short: 'foundations.parameters.proposal.refusal.rationaleTooShort',
  no_review_date: 'foundations.parameters.proposal.refusal.noReviewDate',
  no_valid_from: 'foundations.parameters.proposal.refusal.noValidFrom',
  no_valid_to: 'foundations.parameters.proposal.refusal.noValidTo',
  no_doors_key: 'foundations.modules.refusal.noDoorsKey',
};
